import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

export default function SupportSessionsTab() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('support_messages')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }); // Show newest sessions first
          
        if (error) throw error;
        
        setSessions(data || []);
        // Automatically select the most recent session if available
        if (data && data.length > 0) {
          setActiveSession(data[0]);
        }
      } catch (err) {
        console.error('Error fetching support sessions:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessions();
  }, [user?.id]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading your support history...</div>;
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft overflow-hidden h-[600px] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border bg-muted/30">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="MessageCircle" size={24} className="text-primary" />
          Support Chat History
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Review your past conversations with our support team.</p>
      </div>
      
      {sessions.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-12">
          <Icon name="MessageSquare" size={56} className="text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground font-medium">You have no previous support sessions.</p>
          <p className="text-xs text-muted-foreground mt-1">Past live chat conversations will appear here.</p>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left Pane: Sessions Row-List */}
          <div className="w-[300px] border-r border-border overflow-y-auto flex flex-col bg-slate-50/50 flex-shrink-0">
            <div className="px-4 py-3 bg-muted/20 border-b border-border text-xs font-bold text-slate-500 uppercase tracking-wider">
              Conversations
            </div>
            <div className="divide-y divide-border">
              {sessions.map((session, idx) => {
                const isSelected = activeSession?.session_id === session.session_id;
                const lastMsg = session.messages && session.messages.length > 0 
                  ? session.messages[session.messages.length - 1] 
                  : null;
                const sessionDate = new Date(session.created_at);
                
                return (
                  <div
                    key={session.session_id || idx}
                    onClick={() => setActiveSession(session)}
                    className={`p-4 cursor-pointer select-none transition-all duration-200 border-l-4 ${
                      isSelected 
                        ? 'bg-primary/5 border-primary shadow-sm' 
                        : 'border-transparent hover:bg-slate-100/50'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold text-sm text-foreground">
                        {sessionDate.toLocaleDateString()}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {sessionDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate max-w-[250px]">
                      {lastMsg ? `${lastMsg.role === 'user' ? 'You' : (lastMsg.firstName || 'Support')}: ${lastMsg.text}` : 'Started session'}
                    </p>
                    {session.is_active === false && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold text-[9px] uppercase tracking-wide">
                        Closed
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Pane: Chat History Details */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {activeSession ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Active Session Info Header */}
                <div className="px-6 py-3 border-b border-border bg-slate-50/50 flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Session Details</span>
                    <span className="text-sm font-semibold text-foreground">
                      {new Date(activeSession.created_at).toLocaleString()}
                    </span>
                  </div>
                  {activeSession.is_active === false && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold text-[10px] uppercase tracking-wider">
                      Archived
                    </span>
                  )}
                </div>

                {/* Message Log */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50">
                  {(activeSession.messages || []).map((msg, i) => {
                    const isMe = msg.role === 'user';
                    return (
                      <div key={`${activeSession.session_id}-${i}`} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div 
                          className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                            isMe 
                              ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                              : 'bg-white text-foreground border border-border rounded-tl-sm'
                          }`}
                        >
                          {!isMe && msg.role !== 'system' && (
                            <div className="text-xs font-semibold mb-1 opacity-70">
                              {msg.firstName || msg.username || 'Support'}
                            </div>
                          )}
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.text}</div>
                          <div className="text-[10px] mt-2 opacity-60 text-right">
                            {new Date(msg.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex-grow flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MessageCircle" size={48} className="text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Select a past conversation</p>
                <p className="text-xs text-muted-foreground mt-0.5">Click any session on the left to read full transcript.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}